import { Typography } from '@mui/material';
import useGetEmailsSuggestions from 'hooks/services/email/useGetEmailsSuggestions';
import React, { useEffect, useMemo, useState } from 'react';
import { cn } from 'utils/style-utils';

interface IProps {
  query: string;
  onSelect: (email: string) => void;
  emailsAlreadySelected?: string[] | null; // emails to filter (for cc, bcc fields)
  // Position of the dropdown relative to its positioned ancestor. Defaults to
  // sitting just below a single-line input; pass e.g. "left-0 top-full mt-1"
  // when the anchor is a multi-line (wrapping) row.
  anchorClassName?: string;
}

function escapeRegExp(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const Highlight: React.FC<{ text: string; query?: string }> = ({
  text,
  query,
}) => {
  const q = (query ?? '').trim();

  // Build parts once per text+query
  const parts = useMemo(() => {
    if (!q) return [text];
    const re = new RegExp(`(${escapeRegExp(q)})`, 'ig');
    return text.split(re);
  }, [text, q]);

  return (
    <Typography
      sx={{
        fontSize: '12px',
        lineHeight: '16px',
        fontWeight: 500,
        color: 'inherit', // inherits default and the hover color from parent
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
      title={text}
    >
      {parts.map((part, i) =>
        // Odd indices are the matched fragments due to the capturing group
        q && i % 2 === 1 ? (
          <span key={i} className="text-primary-800">
            {part}
          </span>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        ),
      )}
    </Typography>
  );
};

const EmailAutoComplete = ({
  query,
  onSelect,
  emailsAlreadySelected,
  anchorClassName = 'top-10',
}: IProps) => {
  const [debouncedQuery, setDebouncedQuery] = useState<string>();

  const { data: suggestionsDb } = useGetEmailsSuggestions({
    query: debouncedQuery!,
    enabled: !!debouncedQuery,
  });

  const rawSuggestions: string[] = suggestionsDb?.data.data ?? [];

  const selectedEmailsSet = useMemo(() => {
    if (!emailsAlreadySelected?.length) return new Set<string>();
    return new Set(
      emailsAlreadySelected.map((email) => email.toLowerCase().trim()),
    );
  }, [emailsAlreadySelected]);

  const suggestions = useMemo(() => {
    if (selectedEmailsSet.size === 0) return rawSuggestions;

    return rawSuggestions.filter(
      (email) => !selectedEmailsSet.has(email.toLowerCase().trim()),
    );
  }, [rawSuggestions, selectedEmailsSet]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 500);

    return () => clearTimeout(timer);
  }, [query]);

  if (suggestions.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        'absolute z-100000 p-2 w-75 max-h-52 overflow-y-auto bg-white border border-neutral-300 rounded-lg shadow-lg',
        anchorClassName,
      )}
    >
      {suggestions.map((email) => (
        <div
          key={email}
          className="group p-2 rounded-[8px] cursor-pointer hover:bg-primary-100 hover:text-primary-800 pointer-events-auto"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onSelect(email);
          }}
        >
          <Highlight text={email} query={query} />
        </div>
      ))}
    </div>
  );
};

export default EmailAutoComplete;
